import { Plan, PlanFamily, PlansDurationType, Subscription } from '@localstack/types';
import { Grid, Alert } from '@mui/material';

const PLAN_DURATION_LABELS: Record<PlansDurationType, string> = {
  [PlansDurationType.MONTHLY]: 'monthly',
  [PlansDurationType.YEARLY]: 'yearly',
};

const getPlanDurationLabel = (intervalMonths: number): string =>
  PLAN_DURATION_LABELS[intervalMonths as PlansDurationType] ?? undefined;

/**
 * Component responsible for displaying warnings to the user about the changes they are about to make if the plan change goes ahead.
 * Or warnings to the user around validation errors.
 */
export const CheckoutWarnings = ({
  plan,
  subscription,
  validationError,
}: {
  subscription: Optional<Subscription>;
  plan: Plan;
  validationError: Optional<string>;
}): JSX.Element => {
  const isOldSubscription =
    subscription?.plan?.family &&
    [PlanFamily.PRO_PLANS, PlanFamily.TEAM_PLANS, PlanFamily.LEGACY_PLANS].includes(subscription?.plan?.family);

  // When switching between billing intervals within the same plan family (e.g. Base Yearly → Base Monthly),
  // display the interval label alongside the plan name to distinguish them (e.g. "Base (monthly)")
  const showPlanDurationLabel = subscription?.plan?.name === plan.name;

  return (
    <>
      {!!validationError && (
        <Grid item xs={12}>
          <Alert severity="error">{validationError}</Alert>
        </Grid>
      )}
      {subscription && subscription?.plan.id !== plan.id && (
        <Grid item xs={12}>
          <Alert severity="info">
            You are changing the current subscription Plan from{' '}
            <strong>
              {subscription.plan.name}
              {showPlanDurationLabel && ` (${getPlanDurationLabel(subscription.plan.interval_months)})`}
            </strong>{' '}
            to{' '}
            <strong>
              {plan.name}
              {showPlanDurationLabel && ` (${getPlanDurationLabel(plan.interval_months)})`}
            </strong>
            .
          </Alert>
        </Grid>
      )}
      {subscription && isOldSubscription && (
        <Grid item xs={12}>
          <Alert severity="info">
            Please note that new subscriptions require LocalStack 2026.03 or newer and are not compatible with earlier
            versions.
          </Alert>
        </Grid>
      )}
    </>
  );
};
